import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Zap, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface QuickReply {
  id: string;
  title: string;
  content: string;
  category: string | null;
}

interface QuickRepliesSheetProps {
  onSelect: (content: string) => void;
}

export function QuickRepliesSheet({ onSelect }: QuickRepliesSheetProps) {
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    
    const fetchQuickReplies = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("quick_replies")
        .select("*")
        .order("title", { ascending: true });
      
      if (data) {
        setQuickReplies(data);
      }
      setLoading(false);
    };

    fetchQuickReplies();
  }, [open]);

  const handleSelect = (content: string) => {
    onSelect(content);
    setOpen(false);
  };

  // Group by category
  const groupedReplies = quickReplies.reduce((acc, reply) => {
    const category = reply.category || "Général";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(reply);
    return acc;
  }, {} as Record<string, QuickReply[]>);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Réponses rapides">
          <Zap size={16} />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[60vh]">
        <SheetHeader>
          <SheetTitle className="font-serif flex items-center gap-2">
            <Zap size={18} className="text-primary" />
            Réponses rapides
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-4 overflow-auto h-[calc(100%-4rem)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : quickReplies.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aucune réponse rapide configurée
            </p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedReplies).map(([category, replies]) => (
                <div key={category}>
                  <h3 className="text-xs font-medium text-muted-foreground mb-2 px-1">
                    {category}
                  </h3>
                  <div className="space-y-2">
                    {replies.map((reply) => (
                      <button
                        key={reply.id}
                        onClick={() => handleSelect(reply.content)}
                        className="w-full text-left p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <p className="text-sm font-medium text-foreground">{reply.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{reply.content}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
